// Activity templates (docs/features/activity-templates.md): everything about an activity
// except its dates. Times that hang off the start are stored relative to it, in the same
// units as a recurring template (recurrents/{id}): minutes before the start. Dropping a
// template on the activity calendar turns it back into a normal activity at that time.
//
// Activity datetimes are 'YYYY-MM-DDTHH:mm' strings read as UTC wall clock everywhere,
// so all the arithmetic here stays in moment.utc.

import moment from 'moment'
import { cancellationEndError } from './activityCancellationDeadline'

const FORMAT = 'YYYY-MM-DDTHH:mm'

export const TEMPLATE_DEFAULTS = {
  duration: 60,
  entrystart: 10 * 24 * 60,
  entryend: 12 * 60,
  cancellationend: null,
  publish: false,
  resourceids: [],
  reservationcost: 0,
  // who holds and pays the default court bookings; not the coach, a coach cannot be one
  reservationpayers: [],
}

// Describe one occurrence (or are server-owned) and never belong in a template.
const INSTANCE_FIELDS = [
  'id',
  'creatorId',
  'created',
  'updaterId',
  'updated',
  'timestamp',
  'date',
  'participants',
  'queue',
  'payments',
  'refunds',
  'reservations',
  'recurrentid',
  'excludedFromRecurrent',
  'recurrency',
  'recurrencies',
  'entryunit',
  'collection',
  'entryendProcessed',
  'pinned',
  'state',
  'ispublished',
  'start',
  'end',
  'entrystart',
  'entryend',
  'cancellationend',
]

// Only make sense on the template; dropped when an activity is built from it.
const TEMPLATE_ONLY_FIELDS = [
  'id',
  'creatorId',
  'created',
  'updaterId',
  'updated',
  'state',
  'templatename',
  'duration',
  'publish',
  'resourceids',
  'reservationcost',
  'reservationpayers',
  'entrystart',
  'entryend',
  'cancellationend',
]

const isEmpty = (value) => value === null || value === undefined || value === ''

const clone = (value) => JSON.parse(JSON.stringify(value))

// Question ids are assigned at save (Firebase.resolveActivityExtraInfo); a new document
// must get its own.
const stripExtraInfoIds = (target) => {
  if (Array.isArray(target.extrainfo)) {
    target.extrainfo = target.extrainfo.map(({ id, ...question }) => question)
  }
  return target
}

const minutesBefore = (start, value) => {
  if (isEmpty(value)) return null
  const at = moment.utc(value, FORMAT)
  if (!at.isValid()) return null
  return Math.max(0, start.diff(at, 'minutes'))
}

export const templateFromActivity = (activity) => {
  if (!activity) return null
  const source = { ...activity }
  INSTANCE_FIELDS.forEach((field) => delete source[field])
  const template = stripExtraInfoIds(clone(source))

  const start = moment.utc(activity.start, FORMAT)
  const end = moment.utc(activity.end, FORMAT)
  const hasTimes = start.isValid() && end.isValid() && end.isAfter(start)

  template.templatename = (activity.name && activity.name.fi) || ''
  template.duration = hasTimes ? end.diff(start, 'minutes') : TEMPLATE_DEFAULTS.duration
  template.entrystart = hasTimes ? minutesBefore(start, activity.entrystart) ?? TEMPLATE_DEFAULTS.entrystart : TEMPLATE_DEFAULTS.entrystart
  template.entryend = hasTimes ? minutesBefore(start, activity.entryend) ?? TEMPLATE_DEFAULTS.entryend : TEMPLATE_DEFAULTS.entryend
  template.cancellationend = hasTimes ? minutesBefore(start, activity.cancellationend) : null
  template.publish = activity.state === 'published'
  // courts are booked per activity; the venue picks the template's defaults itself
  template.resourceids = []
  template.reservationcost = TEMPLATE_DEFAULTS.reservationcost
  template.reservationpayers = []
  template.state = 'active'
  return template
}

// start: 'YYYY-MM-DDTHH:mm', the stored UTC wall clock of the new activity's start.
export const activityFromTemplate = (template, start) => {
  if (!template) return null
  const at = moment.utc(start, FORMAT)
  const source = { ...template }
  TEMPLATE_ONLY_FIELDS.forEach((field) => delete source[field])
  const activity = stripExtraInfoIds(clone(source))

  const duration = Number(template.duration) > 0 ? Number(template.duration) : TEMPLATE_DEFAULTS.duration
  const before = (minutes) => at.clone().subtract(Number(minutes) || 0, 'minutes').format(FORMAT)

  activity.start = at.format(FORMAT)
  activity.end = at.clone().add(duration, 'minutes').format(FORMAT)
  activity.entrystart = before(isEmpty(template.entrystart) ? TEMPLATE_DEFAULTS.entrystart : template.entrystart)
  activity.entryend = before(isEmpty(template.entryend) ? TEMPLATE_DEFAULTS.entryend : template.entryend)
  activity.cancellationend = isEmpty(template.cancellationend) ? null : before(template.cancellationend)
  activity.state = template.publish ? 'published' : 'draft'
  activity.ispublished = !!template.publish
  activity.entryendProcessed = false
  activity.pinned = false
  return activity
}

// A recurring series (recurrents/{id}) from a template, as the recurring creator's
// initialActivity. The relative entry/cancellation minutes are already in the series' units
// and carry over unchanged; start/end become times of day. date ('YYYY-MM-DD') and time
// ('HH:mm') come from a calendar drop when there is one. The weekday is returned next to the
// activity because the creator keeps it in its own state, not on the document.
export const DEFAULT_SERIES_TIME = '18:00'

export const recurrentFromTemplate = (template, { date, time } = {}) => {
  if (!template) return null
  const source = { ...template }
  TEMPLATE_ONLY_FIELDS.forEach((field) => delete source[field])
  const activity = stripExtraInfoIds(clone(source))

  const day = date && moment.utc(date, 'YYYY-MM-DD').isValid() ? moment.utc(date, 'YYYY-MM-DD') : moment.utc().startOf('day')
  const startTime = moment.utc(time || DEFAULT_SERIES_TIME, 'HH:mm')
  const duration = Number(template.duration) > 0 ? Number(template.duration) : TEMPLATE_DEFAULTS.duration
  let endTime = startTime.clone().add(duration, 'minutes')
  // a series never crosses midnight: its occurrences are built as <date>T<start>..<date>T<end>
  if (!endTime.isSame(startTime, 'day')) endTime = startTime.clone().endOf('day')

  activity.start = startTime.format('HH:mm')
  activity.end = endTime.format('HH:mm')
  activity.entrystart = isEmpty(template.entrystart) ? TEMPLATE_DEFAULTS.entrystart : Number(template.entrystart)
  activity.entryend = isEmpty(template.entryend) ? TEMPLATE_DEFAULTS.entryend : Number(template.entryend)
  activity.cancellationend = isEmpty(template.cancellationend) ? null : Number(template.cancellationend)
  activity.recurrency = { interval: 'weekly', startdate: day.format('YYYY-MM-DD'), enddate: null }
  activity.state = template.publish ? 'published' : 'draft'
  activity.ispublished = !!template.publish
  activity.entryendProcessed = false
  activity.pinned = false
  return { activity, weekday: day.isoWeekday() }
}

// Moving the start of an activity built from a template keeps enrolment and the cancellation
// deadline at the same distance before it - the template's relative times. Empty fields stay
// empty (a null cancellationend keeps following the entry end).
export const shiftEntryTimes = (activity, previousStart, nextStart) => {
  const from = moment.utc(previousStart, FORMAT)
  const to = moment.utc(nextStart, FORMAT)
  if (!from.isValid() || !to.isValid()) return activity
  const delta = to.diff(from, 'minutes')
  if (!delta) return activity
  const shifted = { ...activity }
  ;['entrystart', 'entryend', 'cancellationend'].forEach((field) => {
    if (isEmpty(shifted[field])) return
    const at = moment.utc(shifted[field], FORMAT)
    if (at.isValid()) shifted[field] = at.add(delta, 'minutes').format(FORMAT)
  })
  return shifted
}

// A sensible first start for a one-off made from the template list: tomorrow at 18:00.
// The user picks the real date in the form; the entry times follow (shiftEntryTimes).
export const defaultTemplateStart = (now = moment.utc()) =>
  moment.utc(now).add(1, 'day').format('YYYY-MM-DD') + 'T' + DEFAULT_SERIES_TIME

export const TEMPLATE_DURATION_ERROR = 'Keston pitää olla yli 0 minuuttia'
export const TEMPLATE_ENTRY_ORDER_ERROR = 'Ilmoittautumisen pitää alkaa ennen kuin se päättyy'

export const templateError = (template) => {
  if (!template) return null
  if (!(Number(template.duration) > 0)) return TEMPLATE_DURATION_ERROR
  if (!isEmpty(template.entrystart) && !isEmpty(template.entryend) && Number(template.entrystart) <= Number(template.entryend)) {
    return TEMPLATE_ENTRY_ORDER_ERROR
  }
  return cancellationEndError(template, { recurrent: true })
}

// e.g. 2 vrk 3 h 30 min ennen alkua; 0 is "alkaessa"
export const formatRelative = (minutes) => {
  if (isEmpty(minutes)) return ''
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  if (total === 0) return 'alkaessa'
  const days = Math.floor(total / (24 * 60))
  const hours = Math.floor((total % (24 * 60)) / 60)
  const mins = total % 60
  const parts = []
  if (days) parts.push(days + ' vrk')
  if (hours) parts.push(hours + ' h')
  if (mins) parts.push(mins + ' min')
  return parts.join(' ') + ' ennen alkua'
}

export const formatDuration = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  const hours = Math.floor(total / 60)
  const mins = total % 60
  if (!hours) return mins + ' min'
  return mins ? hours + ' h ' + mins + ' min' : hours + ' h'
}
